// Point du jour technique — agrégation des chiffres (GET /dashboard/point-jour) par découpage
// et mise en forme du message. Mêmes calculs que la page /dashboard (période de paie 21 → 20).
import { fmtMt, fmtNb, fmtDateFr, decalerMois, evolution } from './format';

// Début du cycle de paie (21 → 20) contenant la date
const debutCycle = (s) => (Number(s.slice(8, 10)) >= 21 ? `${s.slice(0, 8)}21` : decalerMois(`${s.slice(0, 8)}21`, -1));

// Périodes de comparaison de la période de paie (21 → jour de référence). calc null = défaut serveur.
export const COMPARAISONS_T = [
  { value: 'cyclePrec', label: 'Mêmes jours, cycle précédent', calc: null },
  { value: 'anneePrec', label: 'Mêmes dates, année précédente', calc: (date) => ({ start: decalerMois(debutCycle(date), -12), end: decalerMois(date, -12) }) },
  { value: 'perso',     label: 'Personnalisée', calc: null },
];

export const DECOUPAGES_T = [
  { value: 'global',   label: 'Global' },
  { value: 'societe',  label: 'Par société' },
  { value: 'chantier', label: 'Par chantier' },
];

export const BLOCS_T = [
  { id: 'score', label: 'Santé du dashboard', items: [
    { id: 'score.global', label: 'Note globale' },
    { id: 'score.detail', label: 'Détail des axes' },
  ] },
  { id: 'hier', label: 'Hier', items: [
    { id: 'hier.ouvriers',    label: 'Ouvriers pointés' },
    { id: 'hier.cout',        label: 'Coût main-d\'œuvre' },
    { id: 'hier.materiels',   label: 'Matériels pointés' },
    { id: 'hier.gasoil',      label: 'Gasoil' },
    { id: 'hier.ecarts',      label: 'Écarts compteur' },
    { id: 'hier.reparations', label: 'Réparations' },
  ] },
  { id: 'periode', label: 'Période de paie', items: [
    { id: 'periode.ouvriers',    label: '% ouvriers pointés' },
    { id: 'periode.masse',       label: 'Masse salariale' },
    { id: 'periode.jours',       label: 'Jours pointés' },
    { id: 'periode.gasoil',      label: 'Gasoil consommé' },
    { id: 'periode.heures',      label: 'Heures matériel' },
    { id: 'periode.reparations', label: 'Réparations' },
  ] },
  { id: 'comp', label: 'Comparaison', items: [
    { id: 'comp.masse',       label: 'Masse salariale' },
    { id: 'comp.jours',       label: 'Jours pointés' },
    { id: 'comp.gasoil',      label: 'Gasoil' },
    { id: 'comp.heures',      label: 'Heures matériel' },
    { id: 'comp.reparations', label: 'Réparations' },
  ] },
  { id: 'surv', label: 'À surveiller', items: [
    { id: 'surv.vidanges', label: 'Vidanges' },
    { id: 'surv.citernes', label: 'Citernes stock bas' },
    { id: 'surv.panne',    label: 'Matériels en panne / bloqués' },
    { id: 'surv.conso',    label: 'Plus gros consommateurs' },
    { id: 'surv.repTop',   label: 'Réparations les plus chères' },
  ] },
];

export const TYPES_T = [
  { value: 'quotidien', label: 'Quotidien', blocs: [
    'score.global', 'hier.ouvriers', 'hier.cout', 'hier.materiels', 'hier.gasoil', 'hier.ecarts', 'hier.reparations',
    'surv.vidanges', 'surv.citernes', 'surv.panne',
  ] },
  { value: 'hebdo', label: 'Hebdo', blocs: [
    'score.global', 'score.detail', 'periode.ouvriers', 'periode.masse', 'periode.jours', 'periode.gasoil',
    'periode.heures', 'periode.reparations', 'comp.masse', 'comp.gasoil', 'comp.heures',
    'surv.vidanges', 'surv.citernes', 'surv.panne', 'surv.conso', 'surv.repTop',
  ] },
  { value: 'mensuel', label: 'Mensuel', blocs: [
    'score.detail', 'periode.ouvriers', 'periode.masse', 'periode.jours', 'periode.gasoil', 'periode.heures',
    'periode.reparations', 'comp.masse', 'comp.jours', 'comp.gasoil', 'comp.heures', 'comp.reparations',
    'surv.vidanges', 'surv.conso', 'surv.repTop',
  ] },
];

// Appréciation du score — mêmes paliers que la page /dashboard
export const noteScore = (p) => {
  if (p >= 90) return { emoji: '👏', label: 'Excellence',  color: '#1b5e20' };
  if (p >= 80) return { emoji: '👍', label: 'Bon travail', color: '#2e7d32' };
  if (p >= 50) return { emoji: '🤏', label: 'Peut mieux',  color: '#e65100' };
  return              { emoji: '👎', label: 'Alerte',      color: '#b71c1c' };
};

export const fmtL = (v) => `${fmtNb(v)} L`;
export const fmtH = (v) => `${fmtNb(v)} h`;

/* ─── Agrégation ──────────────────────────────────────────────────────────── */
const sumBy = (rows, ids, f) => rows.reduce((s, r) => (ids.has(r.chantierId) ? s + (r[f] || 0) : s), 0);
const countBy = (rows, ids, pred = () => true) => rows.reduce((s, r) => (ids.has(r.chantierId) && pred(r) ? s + 1 : s), 0);

// Chiffres d'un ensemble de chantiers
export function aggChantiers(data, ids) {
  const h = data.hier, p = data.periodeChiffres, D = data.details;
  return {
    hier: {
      ouvNb: sumBy(h.ouvriers, ids, 'nb'), ouvJours: sumBy(h.ouvriers, ids, 'jours'), ouvHeures: sumBy(h.ouvriers, ids, 'heures'),
      cout: sumBy(h.ouvriers, ids, 'cout'),
      matNb: sumBy(h.materiels, ids, 'nb'), matHeures: sumBy(h.materiels, ids, 'heures'),
      litres: sumBy(h.materiels, ids, 'litres'), ecarts: sumBy(h.materiels, ids, 'ecarts'),
      repNb: sumBy(h.reparations, ids, 'nb'), repMt: sumBy(h.reparations, ids, 'montant'),
    },
    periode: {
      masse: sumBy(p.masse, ids, 'masse'), jours: sumBy(p.pointages, ids, 'jours'), heuresSup: sumBy(p.pointages, ids, 'heures'),
      litres: sumBy(p.gasoil, ids, 'litres'), heuresMat: sumBy(p.heuresMateriel, ids, 'heures'),
      repNb: sumBy(p.reparations, ids, 'nb'), repMt: sumBy(p.reparations, ids, 'montant'),
    },
    surv: {
      vidDepasse: countBy(D.vidanges, ids, v => v.niveau === 'depasse'),
      vidCritique: countBy(D.vidanges, ids, v => v.niveau === 'critique'),
      vidAlerte: countBy(D.vidanges, ids, v => v.niveau === 'alerte'),
      citernesBas: countBy(D.citernes, ids, c => c.bas),
      stock: sumBy(D.citernes, ids, 'stock'),
    },
  };
}

// Groupes selon le découpage : [{ key, label, societe?, ids:Set, agg }]
export function buildGroupesT(data, decoupage) {
  const all = new Set(data.chantiers.map(c => c.id));
  const total = { key: 'total', label: 'Total', ids: all, agg: aggChantiers(data, all) };
  if (decoupage === 'societe') {
    const societes = new Map();
    for (const c of data.chantiers) {
      if (!societes.has(c.societe)) societes.set(c.societe, { key: `s_${c.societe}`, label: c.societe, ids: new Set() });
      societes.get(c.societe).ids.add(c.id);
    }
    return { groupes: [...societes.values()].map(g => ({ ...g, agg: aggChantiers(data, g.ids) })), total };
  }
  if (decoupage === 'chantier') {
    return {
      groupes: data.chantiers.map(c => {
        const ids = new Set([c.id]);
        return { key: `c${c.id}`, label: c.nom, societe: c.societe, ids, agg: aggChantiers(data, ids) };
      }),
      total,
    };
  }
  return { groupes: [], total };
}

export const pctOuvriers = (data) => {
  const o = data.periodeChiffres.ouvriers;
  return o.nonBloques > 0 ? Math.min(100, Math.round((o.actifsNonBloques * 100) / o.nonBloques)) : null;
};

// Valeur brute derrière chaque KPI « à comptage » du score (la note baisse quand le nombre monte)
export const valeursKpi = (data) => {
  const v = data.details.vidanges;
  return {
    'DASH-04': data.etat.citernesBas,
    'DASH-05': v.filter(x => x.niveau === 'depasse').length,
    'DASH-06': v.filter(x => x.niveau === 'critique').length,
    'DASH-07': v.filter(x => x.niveau === 'alerte').length,
    'DASH-08': data.etat.enPanne,
    'DASH-11': data.periodeChiffres.reparations.reduce((s, r) => s + r.nb, 0),
  };
};

/* ─── Texte du message ────────────────────────────────────────────────────── */
const hasAny = (blocs, prefix) => [...blocs].some(b => b.startsWith(prefix));
const MAX_LIGNES = 8;
const liste = (rows, ligneFn, max = MAX_LIGNES) => {
  const out = rows.slice(0, max).map(r => `   – ${ligneFn(r).filter(Boolean).join(' · ')}`);
  if (rows.length > max) out.push(`   … +${rows.length - max} autre${rows.length - max > 1 ? 's' : ''}`);
  return out;
};

export function buildTexteTechnique(data, { decoupage = 'global', format = 'standard', blocs: blocsArr = [], titre } = {}) {
  if (!data) return '';
  const blocs = new Set(blocsArr);
  const { groupes, total } = buildGroupesT(data, decoupage);
  const T = total.agg;
  const det = format === 'detaille';
  const nomChantier = (id) => data.chantiers.find(c => c.id === id)?.nom || 'Sans chantier';
  const L = [];
  const section = (title, lines) => { if (lines.length) L.push('', title, ...lines); };
  const dansPerimetre = (r) => total.ids.has(r.chantierId);
  // Lignes par groupe (société / chantier), groupes vides ignorés, puis total
  const parGroupe = (ligneFn, vide) => {
    const lines = groupes.filter(g => !vide(g.agg)).map(g => `${decoupage === 'societe' ? '🏢' : '•'} ${g.label} : ${ligneFn(g.agg)}`);
    if (!lines.length) lines.push('Aucun mouvement');
    lines.push(`= Total : ${ligneFn(T)}`);
    return lines;
  };

  L.push(`🛠 ${titre || 'SHEMS TECHNIQUE'} — ${fmtDateFr(data.date)}`);

  // ── Score ──
  if (hasAny(blocs, 'score.')) {
    const lines = [];
    const n = noteScore(data.score.global);
    if (blocs.has('score.global')) {
      const diff = data.score.global - data.scoreVeille.global;
      lines.push(`⭐ ${data.score.axes ? 'Santé' : 'Score dashboard'} : ${data.score.global}/100 ${n.emoji} ${n.label}${diff ? ` (${diff > 0 ? '+' : ''}${diff} vs veille)` : ''}`);
    }
    if (blocs.has('score.detail')) {
      const pastille = (s) => (s >= 80 ? '🟢' : s >= 50 ? '🟠' : '🔴');
      if (data.score.axes) {
        // Score v2 (santé du dashboard) : taux par axe
        data.score.axes.forEach(a => lines.push(`   ${pastille(a.score)} ${a.label} : ${a.score} % (${fmtNb(a.valeur)}/${fmtNb(a.total)})`));
      } else {
        // Rapports enregistrés avant le score v2 (KPI à comptage)
        const nb = valeursKpi(data);
        data.score.kpis.forEach(k => lines.push(
          `   ${pastille(k.score)} ${k.label} : ${k.unit === '%' ? `${k.score} %` : `${fmtNb(nb[k.code])} → note ${k.score}/100`}`
        ));
      }
    }
    if (lines.length) L.push('', ...lines);
  }

  // ── Hier ──
  if (hasAny(blocs, 'hier.')) {
    // Valeurs nulles omises dans les lignes par groupe (pas de « 0 L » pour un chantier sans matériel)
    const ligne = (a) => [
      blocs.has('hier.ouvriers') && a.hier.ouvNb ? `👷 ${fmtNb(a.hier.ouvNb)}` : null,
      blocs.has('hier.cout') && a.hier.cout ? `${fmtMt(a.hier.cout)} DH` : null,
      blocs.has('hier.materiels') && a.hier.matNb ? `🚜 ${fmtNb(a.hier.matNb)} (${fmtH(a.hier.matHeures)})` : null,
      blocs.has('hier.gasoil') && a.hier.litres ? `⛽ ${fmtL(a.hier.litres)}` : null,
      blocs.has('hier.reparations') && a.hier.repNb ? `🔧 ${fmtMt(a.hier.repMt)} DH` : null,
    ].filter(Boolean).join(' · ');
    let lines;
    if (decoupage === 'global' || format === 'court') {
      lines = format === 'court' ? [ligne(T)] : [
        blocs.has('hier.ouvriers') ? `• Ouvriers pointés : ${fmtNb(T.hier.ouvNb)} (${fmtNb(T.hier.ouvJours)} j, ${fmtNb(T.hier.ouvHeures)} h sup)` : null,
        blocs.has('hier.cout') ? `• Coût main-d'œuvre : ${fmtMt(T.hier.cout)} DH` : null,
        blocs.has('hier.materiels') ? `• Matériels pointés : ${fmtNb(T.hier.matNb)} — ${fmtH(T.hier.matHeures)}` : null,
        blocs.has('hier.gasoil') ? `• Gasoil : ${fmtL(T.hier.litres)}` : null,
        blocs.has('hier.reparations') ? `• Réparations : ${T.hier.repNb ? `${fmtNb(T.hier.repNb)} ligne(s) — ${fmtMt(T.hier.repMt)} DH` : 'aucune'}` : null,
      ].filter(Boolean);
    } else {
      lines = parGroupe(ligne, a => !a.hier.ouvNb && !a.hier.matNb && !a.hier.repNb);
    }
    if (blocs.has('hier.ecarts') && T.hier.ecarts) lines.push(`• ⚠️ Écarts compteur suspects : ${fmtNb(T.hier.ecarts)}`);
    if (det) {
      const D = data.details;
      if (blocs.has('hier.ecarts')) {
        const ecarts = D.materielsHier.filter(r => r.ecart && dansPerimetre(r));
        if (ecarts.length) lines.push(...liste(ecarts, r => [r.matricule, nomChantier(r.chantierId), r.ecartMessage]));
      }
      if (blocs.has('hier.reparations')) {
        const reps = D.reparationsHier.filter(dansPerimetre);
        if (reps.length) lines.push('', '📝 Réparations :', ...liste(reps, r => [r.matricule, r.designation, r.fournisseur, `${fmtMt(r.montant)} DH`]));
      }
      if (blocs.has('hier.gasoil')) {
        const gros = D.materielsHier.filter(r => r.litres > 0 && dansPerimetre(r)).sort((a, b) => b.litres - a.litres);
        if (gros.length) lines.push('', '📝 Plus gros pleins :', ...liste(gros, r => [r.matricule, nomChantier(r.chantierId), fmtL(r.litres), `${fmtH(r.heures)}`], 5));
      }
    }
    section('🗓 HIER', lines);
  }

  // ── Période de paie ──
  if (hasAny(blocs, 'periode.')) {
    const ligne = (a) => [
      blocs.has('periode.masse') && a.periode.masse ? `${fmtMt(a.periode.masse)} DH` : null,
      blocs.has('periode.jours') && a.periode.jours ? `${fmtNb(a.periode.jours)} j` : null,
      blocs.has('periode.gasoil') && a.periode.litres ? `⛽ ${fmtL(a.periode.litres)}` : null,
      blocs.has('periode.heures') && a.periode.heuresMat ? `🚜 ${fmtH(a.periode.heuresMat)}` : null,
      blocs.has('periode.reparations') && a.periode.repMt ? `🔧 ${fmtMt(a.periode.repMt)} DH` : null,
    ].filter(Boolean).join(' · ');
    const lines = [];
    const o = data.periodeChiffres.ouvriers;
    if (blocs.has('periode.ouvriers')) {
      lines.push(`• Ouvriers pointés : ${fmtNb(o.actifsNonBloques)} / ${fmtNb(o.nonBloques)} (${pctOuvriers(data) ?? '—'} %)`);
    }
    if (decoupage === 'global' || format === 'court') {
      if (format === 'court') lines.push(ligne(T));
      else {
        if (blocs.has('periode.masse')) lines.push(`• Masse salariale : ${fmtMt(T.periode.masse)} DH`);
        if (blocs.has('periode.jours')) lines.push(`• Jours pointés : ${fmtNb(T.periode.jours)} (${fmtNb(T.periode.heuresSup)} h sup)`);
        if (blocs.has('periode.gasoil')) lines.push(`• Gasoil consommé : ${fmtL(T.periode.litres)}`);
        if (blocs.has('periode.heures')) lines.push(`• Heures matériel : ${fmtH(T.periode.heuresMat)}`);
        if (blocs.has('periode.reparations')) lines.push(`• Réparations : ${fmtNb(T.periode.repNb)} ligne(s) — ${fmtMt(T.periode.repMt)} DH`);
      }
    } else {
      lines.push(...parGroupe(ligne, a => !a.periode.masse && !a.periode.jours && !a.periode.litres && !a.periode.repMt));
    }
    section(`📅 PÉRIODE DE PAIE (${fmtDateFr(data.periode.start).slice(0, 5)} → ${fmtDateFr(data.date).slice(0, 5)})`, lines);
  }

  // ── Comparaison de la période de paie (global, même calcul pour les deux périodes) ──
  if (hasAny(blocs, 'comp.') && data.comparaison) {
    const { actuel: a, reference: r, start, end } = data.comparaison;
    const lignes = [
      ['comp.masse', 'Masse salariale', a.masse, r.masse, v => `${fmtMt(v)} DH`],
      ['comp.jours', 'Jours pointés', a.jours, r.jours, fmtNb],
      ['comp.gasoil', 'Gasoil', a.litres, r.litres, fmtL],
      ['comp.heures', 'Heures matériel', a.heuresMateriel, r.heuresMateriel, fmtH],
      ['comp.reparations', 'Réparations', a.reparations, r.reparations, v => `${fmtMt(v)} DH`],
    ].filter(([id]) => blocs.has(id))
      .map(([, label, va, vr, f]) => `• ${label} : ${f(va)} vs ${f(vr)}${evolution(va, vr)}`);
    section(`↔️ VS DU ${fmtDateFr(start)} AU ${fmtDateFr(end)}`, lignes);
  }

  // ── À surveiller ──
  if (hasAny(blocs, 'surv.')) {
    const lines = [];
    const D = data.details;
    const S = T.surv;
    if (blocs.has('surv.vidanges') && (S.vidDepasse || S.vidCritique || S.vidAlerte)) {
      lines.push(`• Vidanges : ${fmtNb(S.vidDepasse)} dépassées · ${fmtNb(S.vidCritique)} critiques · ${fmtNb(S.vidAlerte)} alertes`);
      if (decoupage !== 'global' && format !== 'court') {
        groupes.filter(g => g.agg.surv.vidDepasse).sort((a, b) => b.agg.surv.vidDepasse - a.agg.surv.vidDepasse).slice(0, 5)
          .forEach(g => lines.push(`   – ${g.label} : ${fmtNb(g.agg.surv.vidDepasse)} dépassée(s)`));
      }
      if (det) {
        const dep = D.vidanges.filter(v => v.niveau === 'depasse' && dansPerimetre(v)).sort((a, b) => a.reste - b.reste);
        lines.push(...liste(dep, v => [v.matricule, v.type, nomChantier(v.chantierId), `${fmtNb(v.cycle)}/${fmtNb(v.interval)} ${v.mode}`]));
      }
    }
    if (blocs.has('surv.citernes') && S.citernesBas) {
      lines.push(`• ${fmtNb(S.citernesBas)} citerne(s) en stock bas — stock total ${fmtL(S.stock)}`);
      if (det || format === 'standard') {
        const bas = D.citernes.filter(c => c.bas && dansPerimetre(c)).sort((a, b) => a.stock - b.stock);
        lines.push(...liste(bas, c => [c.nom, `${fmtL(c.stock)} (min ${fmtL(c.min)})`], det ? MAX_LIGNES : 3));
      }
    }
    if (blocs.has('surv.panne')) {
      const e = data.etat;
      lines.push(`• Matériels en panne : ${fmtNb(e.enPanne)} · bloqués (hors parc) : ${fmtNb(e.bloques)} · en parc : ${fmtNb(e.totalMateriels)}`);
    }
    if (blocs.has('surv.conso')) {
      const top = D.gasoilPeriode.filter(g => g.litres > 0 && dansPerimetre(g)).sort((a, b) => b.litres - a.litres);
      if (top.length) {
        lines.push('• Plus gros consommateurs (période) :');
        lines.push(...liste(top, g => [g.matricule, nomChantier(g.chantierId), fmtL(g.litres), g.litresHeure != null ? `${String(g.litresHeure).replace('.', ',')} L/h` : null], det ? 8 : 3));
      }
    }
    if (blocs.has('surv.repTop')) {
      const top = D.reparationsPeriode.filter(dansPerimetre).sort((a, b) => b.montant - a.montant);
      if (top.length) {
        lines.push('• Réparations les plus chères (période) :');
        lines.push(...liste(top, r => [r.matricule, nomChantier(r.chantierId), `${fmtMt(r.montant)} DH`, det ? r.fournisseurs : null], det ? 8 : 3));
      }
    }
    if (!lines.length && format !== 'court') lines.push('Rien à signaler ✅');
    section('🔔 À SURVEILLER', lines);
  }

  return L.join('\n');
}
